function isWordCharacter(character: string | undefined): boolean {
  return Boolean(character && /[A-Za-z0-9À-ÖØ-öø-ÿ]/.test(character));
}

function previousNonWhitespace(text: string, index: number): string | undefined {
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    if (!/\s/.test(text[cursor])) return text[cursor];
  }
  return undefined;
}

function nextNonWhitespace(text: string, index: number): string | undefined {
  for (let cursor = index + 1; cursor < text.length; cursor += 1) {
    if (!/\s/.test(text[cursor])) return text[cursor];
  }
  return undefined;
}

/**
 * Normalizes punctuation in generated prose at render time. Existing curly
 * punctuation is preserved, while JSON/source text can continue using ASCII.
 */
export function formatTypographicQuotes(value: string | null | undefined): string {
  if (!value) return value ?? "";

  let result = "";
  const quoteStack: Array<"double" | "single"> = [];

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];

    if (character === "“") {
      quoteStack.push("double");
      result += character;
      continue;
    }

    if (character === "”") {
      quoteStack.pop();
      result += character;
      continue;
    }

    if (character === "‘") {
      quoteStack.push("single");
      result += character;
      continue;
    }

    if (character === "’") {
      const previous = value[index - 1];
      const next = value[index + 1];
      if (!(isWordCharacter(previous) && isWordCharacter(next)) && quoteStack.at(-1) === "single") {
        quoteStack.pop();
      }
      result += character;
      continue;
    }

    if (character === "\"") {
      const previous = value[index - 1];
      const next = nextNonWhitespace(value, index);
      const opensQuote = Boolean(next) && (!previous || /[\s([{—–-]/.test(previous));

      if (opensQuote) {
        const nested = quoteStack.length % 2 === 1;
        result += nested ? "‘" : "“";
        quoteStack.push(nested ? "single" : "double");
      } else {
        const openType = quoteStack.pop();
        result += openType === "single" ? "’" : "”";
      }
      continue;
    }

    if (character === "'") {
      const previous = value[index - 1];
      const next = value[index + 1];

      const leadingApostrophe = !isWordCharacter(previous)
        && /^(?:cause|em|round|til|twas)\b/i.test(value.slice(index + 1));

      if ((isWordCharacter(previous) && isWordCharacter(next)) || leadingApostrophe) {
        result += "’";
        continue;
      }

      const nextVisible = nextNonWhitespace(value, index);
      const opensQuote = Boolean(nextVisible) && (!previous || /[\s([{—–-]/.test(previous));

      if (opensQuote) {
        result += "‘";
        quoteStack.push("single");
      } else {
        quoteStack.pop();
        result += "’";
      }
      continue;
    }

    result += character;
  }

  return result;
}